import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F6F3]">
      {/* Simple top bar */}
      <header className="bg-white border-b border-stone-100 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/">
            <img
              src="https://storage.readdy-site.link/project_files/1b4b5470-43ee-4e4c-a03f-80bf782ac886/295ea2cc-8770-43e6-ae89-4a5f4095a2e1_PAWZLY-7.png?v=947d967eac3d6db96af148317019d39e"
              alt="Pawzly Logo"
              className="h-16 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-stone-500 hover:text-stone-800 text-sm transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="bg-white rounded-3xl p-8 md:p-14 border border-stone-100">
          <div className="mb-10">
            <h1 className="text-4xl font-bold text-stone-800 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Privacy Policy
            </h1>
            <p className="text-stone-400 text-sm">Last updated: April 3, 2026</p>
          </div>

          <div className="prose prose-stone max-w-none space-y-10 text-stone-600 text-sm leading-relaxed">

            <section>
              <h2 className="text-lg font-bold text-stone-800 mb-3">1. Introduction</h2>
              <p>
                Welcome to Pawzly ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and make purchases from us.
              </p>
              <p className="mt-3">
                Please read this policy carefully. If you disagree with its terms, please discontinue use of our site.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 mb-3">2. Information We Collect</h2>
              <p className="mb-3">We collect information you provide directly to us, including:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Personal identifiers:</strong> Name, email address, shipping address, billing address, and phone number when you place an order.</li>
                <li><strong>Payment information:</strong> Credit card details and billing information. Note: payment processing is handled securely by Shopify Payments — we do not store your full card details on our servers.</li>
                <li><strong>Communications:</strong> Any messages you send us via email or contact forms.</li>
                <li><strong>Newsletter subscriptions:</strong> Your email address if you opt in to our mailing list.</li>
              </ul>
              <p className="mt-3">We also automatically collect certain information when you visit our site:</p>
              <ul className="list-disc pl-5 space-y-2 mt-2">
                <li>IP address, browser type, and operating system</li>
                <li>Pages visited, time spent on pages, and referring URLs</li>
                <li>Device identifiers and cookie data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 mb-3">3. How We Use Your Information</h2>
              <p className="mb-3">We use the information we collect to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Process and fulfill your orders, including sending order confirmations and shipping updates</li>
                <li>Communicate with you about your purchases and respond to inquiries</li>
                <li>Send promotional emails and newsletters (only if you have opted in)</li>
                <li>Improve our website, products, and customer experience</li>
                <li>Detect and prevent fraud or unauthorized transactions</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 mb-3">4. Sharing Your Information</h2>
              <p className="mb-3">We do not sell your personal information. We may share your information with:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Shopify:</strong> Our e-commerce platform provider, which processes orders and payments on our behalf.</li>
                <li><strong>Shipping carriers:</strong> Such as USPS, UPS, or FedEx, to deliver your orders.</li>
                <li><strong>Email service providers:</strong> To send transactional and marketing emails.</li>
                <li><strong>Analytics providers:</strong> To help us understand how our website is used.</li>
                <li><strong>Legal authorities:</strong> When required by law or to protect our rights.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 mb-3">5. Cookies</h2>
              <p>
                We use cookies and similar tracking technologies to enhance your browsing experience, analyze site traffic, and personalize content. You can control cookie settings through your browser preferences. Disabling cookies may affect some functionality of our site.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 mb-3">6. Data Retention</h2>
              <p>
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required by law. Order information is typically retained for 7 years for tax and accounting purposes.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 mb-3">7. Your Rights</h2>
              <p className="mb-3">Depending on your location, you may have the right to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your personal information</li>
                <li>Opt out of marketing communications at any time</li>
                <li>Lodge a complaint with your local data protection authority</li>
              </ul>
              <p className="mt-3">
                To exercise any of these rights, please contact us at the email address below.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 mb-3">8. Children&apos;s Privacy</h2>
              <p>
                Our website is not directed to children under the age of 13. We do not knowingly collect personal information from children. If you believe we have inadvertently collected such information, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 mb-3">9. Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 mb-3">10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any significant changes by updating the "Last updated" date at the top of this page. Your continued use of our website after changes are posted constitutes your acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold text-stone-800 mb-3">11. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="mt-4 p-5 bg-[#F8F6F3] rounded-xl border border-stone-100">
                <p className="font-semibold text-stone-800">Pawzly</p>
                <p className="mt-1">Email: <a href="mailto:hello@pawzly.com" className="text-[#C4956A] hover:underline">hello@pawzly.com</a></p>
              </div>
            </section>

          </div>
        </div>

        <div className="text-center mt-10">
          <Link
            to="/terms"
            className="text-[#8A9B85] hover:text-stone-800 text-sm transition-colors cursor-pointer underline underline-offset-4"
          >
            View Terms of Service
          </Link>
        </div>
      </main>
    </div>
  );
}
